/**
 * Tiny hash router. Routes look like "#/projects/:id". Patterns use ":name"
 * segments which become string params. The first matching route wins.
 *
 * A QUERY STRING IS NOT PART OF THE PATH. `#/rooms/<id>?tab=punch` routes to
 * `/rooms/:id` with `id` = the room id alone; the view reads `?tab=` off
 * `location.hash` itself.
 *
 * That is not a nicety. `([^/]+)` happily swallows `?tab=punch`, so before
 * 2026-09-22 such a link matched with `id = "<uuid>?tab=punch"` — a uuid
 * Postgres rejects — and every "Open in room" link landed on "Failed to load".
 * The query had never been navigated to before, only written with
 * history.replaceState, so nothing caught it.
 */

export interface Route {
  pattern: string;
  render: (params: Record<string, string>) => void | Promise<void>;
}

interface CompiledRoute {
  regex: RegExp;
  keys: string[];
  render: Route['render'];
}

function compile(pattern: string): CompiledRoute {
  const keys: string[] = [];
  const regex = new RegExp('^' + pattern.replace(/:([^/]+)/g, (_, k) => {
    keys.push(k);
    return '([^/]+)';
  }) + '/?$');
  return { regex, keys, render: () => {} };
}

/** The routable part of a hash: no leading '#', no query string, no fragment. */
export function pathFromHash(hash: string): string {
  const raw = hash.replace(/^#/, '');
  // Split on the FIRST '?' or '#': everything after it belongs to the view.
  const path = raw.split(/[?#]/)[0] ?? '';
  return path || '/';
}

export function createRouter(routes: Route[], notFound: () => void): {
  start: () => void;
  navigate: (path: string) => void;
} {
  const compiled: CompiledRoute[] = routes.map((r) => ({
    ...compile(r.pattern),
    render: r.render,
  }));

  const handle = (): void => {
    const path = pathFromHash(location.hash);
    for (const route of compiled) {
      const match = route.regex.exec(path);
      if (match) {
        const params: Record<string, string> = {};
        route.keys.forEach((k, i) => { params[k] = decodeURIComponent(match[i + 1]); });
        void route.render(params);
        return;
      }
    }
    notFound();
  };

  return {
    start: () => {
      window.addEventListener('hashchange', handle);
      handle();
    },
    navigate: (path: string) => {
      if (location.hash === '#' + path) handle();
      else location.hash = '#' + path;
    },
  };
}
